import dayjs from 'dayjs'
import i18next from 'i18next'
import { ThemeType } from 'models'
import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { setThemeMode } from 'store/actions/ui.action'
import { useAppSelector } from 'store/useStoreHook'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import toObject from 'dayjs/plugin/toObject'
import isoWeek from 'dayjs/plugin/isoWeek'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import duration from 'dayjs/plugin/duration'

// Extend Day.js with plugins
// after extend is global—it applies to all dayjs() instances.
// You only need to call it once
// It does not override previous extensions—it adds new features.
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(localizedFormat)
dayjs.extend(toObject)
dayjs.extend(isoWeek)
dayjs.extend(customParseFormat)
dayjs.extend(isBetween)
dayjs.extend(duration)

export const useGloballyConfig = () => {
    const dispatch = useDispatch()

    const country = useAppSelector((state) => state.operation.currentCountry)
    const userData = useAppSelector((state) => state.auth.user)
    const operationSetting = useAppSelector((state) => state.operation.data)
    const language = useAppSelector((state) => state.operation.data.language)
    const currentLang = useMemo(() => {
        return userData?.userPreference?.language ?? (language || 'en')
    }, [language, userData?.userPreference?.language])

    const timeLang = useMemo(() => {
        const code = country?.code
        switch (code) {
            case 'TH':
                return 'th'
            case 'US':
                return 'en'
            default:
                return currentLang
        }
    }, [country, currentLang])

    useEffect(() => {
        //set theme mode
        dispatch(
            setThemeMode(
                (userData?.userPreference?.theme ?? ThemeType.Light) ===
                    ThemeType.Light,
            ),
        )
    }, [dispatch, userData?.userPreference?.theme])

    useEffect(() => {
        //setting dayjs language and format
        dayjs.locale(timeLang ?? i18next.language)
    }, [timeLang])

    useEffect(() => {
        let lang: string | null | undefined = null
        if ((userData?.userPreference?.language ?? null) !== null)
            lang = userData?.userPreference?.language
        else lang = operationSetting.language
        //setting client language
        i18next.changeLanguage(lang ?? i18next.language)
    }, [
        dispatch,
        userData?.userPreference?.language,
        operationSetting.language,
    ])
}
